/* eslint-disable max-lines */
import { ElementRef } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { AddAddressContent } from '../login-modal/add-address/models/add-address.interface';
import { AddEmailAddressContent } from '../login-modal/add-email-address/models/add-email-address.interface';
import { AddSqaContent } from '../login-modal/add-sqa/modals/add-sqa.interface';
import { CreateMyPasswordContent } from '../login-modal/create-my-password/model/create-my-password';
import { ForgotLoginScreenContent } from '../login-modal/forgot-login/modals/forgot-login.interface';
import { LoginAuthenticationScreen } from '../login-modal/login-authentication-screen/models/login-credentials.interface';
import { sqaContent } from '../login-modal/recover-by-security-questions/models/recover-by-security-questions.interface';
import {
  ResetPasswordContent,
  ResetPasswordFormAttributes
} from '../login-modal/reset-password/model/reset-password.interface';

export class LoginFormValidate {
  static validateFormFields(formGroup: UntypedFormGroup): number {
    let errorCount = 0;
    for (const key of Object.keys(formGroup.controls)) {
      const control: AbstractControl = formGroup.controls[key];
      if (control.enabled && control.errors) {
        errorCount += 1;
      }
    }
    return errorCount;
  }

  static getErrorAlert(formGroup: UntypedFormGroup): boolean {
    const errorCount = LoginFormValidate.validateFormFields(formGroup);
    if (errorCount < 1) {
      return false;
    } else return true;
  }

  static focusFirstInvalidFieldHandler(
    formGroup: UntypedFormGroup,
    elementRef: ElementRef
  ): void {
    for (const key of Object.keys(formGroup.controls)) {
      if (
        formGroup.controls[key].enabled &&
        formGroup.controls[key].status === 'INVALID'
      ) {
        const query = 'input:not([type="file"])';

        elementRef.nativeElement
          .querySelector('[formControlName="' + key + '"]')
          .querySelector(query)
          .focus();
        break;
      }
    }
  }

  static touchControl(
    fieldName: string,
    loginContent: LoginAuthenticationScreen
  ) {
    if (fieldName === 'userId')
      loginContent.loginFormAttributes.userIdControlTouched = true;
    if (fieldName === 'password')
      loginContent.loginFormAttributes.passwordControlTouched = true;
    if (fieldName === 'lastName')
      loginContent.loginFormAttributes.lastNameControlTouched = true;
  }

  static touchControlSecurityscreen(
    fieldName: string,
    securityContent: sqaContent
  ) {
    if (fieldName === 'answer1')
      securityContent.sqaFormAttributes.answer1ControlTouched = true;
    if (fieldName === 'answer2')
      securityContent.sqaFormAttributes.answer2ControlTouched = true;
  }

  static resetPasswordFormTouchControl(
    fieldName: string,
    content: ResetPasswordContent,
    setValue: boolean
  ) {
    const controlName = `${fieldName}ControlTouched`;
    content.formAttributes = {
      ...({} as ResetPasswordFormAttributes),
      ...content.formAttributes
    };
    content.formAttributes[controlName] = setValue;
  }

  static touchForgotLoginControl(
    fieldName: string,
    forgotLoginContent: ForgotLoginScreenContent
  ) {
    if (fieldName === 'emailId')
      forgotLoginContent.forgotLoginAttributes.emailIdControlTouched = true;
    if (fieldName === 'firstName')
      forgotLoginContent.forgotLoginAttributes.firstNameControlTouched = true;
    if (fieldName === 'lastName')
      forgotLoginContent.forgotLoginAttributes.lastNameControlTouched = true;
  }

  static touchCreateMyPasswordControl(
    fieldName: string,
    createMyPasswordContent: CreateMyPasswordContent
  ) {
    if (fieldName === 'newPassword') {
      createMyPasswordContent.formAttributes.newPasswordControlTouched = true;
    }
    if (fieldName === 'confirmPassword') {
      createMyPasswordContent.formAttributes.confirmPasswordControlTouched =
        true;
    }
  }

  static touchControlAddEmailAddress(
    fieldName: string,
    content: AddEmailAddressContent
  ) {
    const prop = `${fieldName}ControlTouched`;
    content.formAttributes[prop] = true;
  }

  static touchControlAddAddress(fieldName: string, content: AddAddressContent) {
    const controlMap = {
      addressLine1: 'addressLine1ControlTouched',
      addressLine2: 'addressLine2ControlTouched',
      country: 'countryControlTouched',
      district: 'districtControlTouched',
      city: 'cityControlTouched',
      state: 'stateControlTouched',
      postalCode: 'postalCodeControlTouched'
    };
    const controlKey = <string>controlMap[fieldName];
    if (controlKey) {
      content.formAttributes[controlKey] = true;
    }
  }

  static touchControlsAddAddressOnSubmit(addressContent: AddAddressContent) {
    for (const key of Object.keys(addressContent.formGroup.controls)) {
      const control = addressContent.formGroup?.controls[key];
      if (control.enabled && control.untouched) {
        control.markAsTouched();
        this.touchControlAddAddress(key, addressContent);
      }
    }
  }

  static touchControlsOnSubmit(loginContent: LoginAuthenticationScreen) {
    for (const key of Object.keys(loginContent.formGroup.controls)) {
      const control = loginContent.formGroup?.controls[key];
      if (control.enabled && control.untouched) {
        control.markAsTouched();
        this.touchControl(key, loginContent);
      }
    }
  }

  static sqaControlsOnSubmit(content: sqaContent) {
    for (const key of Object.keys(content.formGroup.controls)) {
      const control = content.formGroup?.controls[key];
      if (control.enabled && control.untouched) {
        control.markAsTouched();
        this.touchControlSecurityscreen(key, content);
      }
    }
  }

  static resetPasswordFormControlsOnSubmit(
    content: ResetPasswordContent,
    formControl: string,
    formSubmit = false
  ) {
    if (!formSubmit) {
      this.updateTouchControl(content, formControl);
    } else {
      for (const key of Object.keys(content.formGroup.controls)) {
        const control = content.formGroup?.controls[key];
        if (control.enabled) {
          control.markAsTouched();
          this.resetPasswordFormTouchControl(key, content, formSubmit);
        }
      }
    }
  }

  static updateTouchControl(
    content: ResetPasswordContent,
    formControl: string
  ) {
    const control = content.formGroup?.controls[formControl];
    const touchValue =
      control?.enabled &&
      ![undefined, ''].includes(control?.value?.value.trim());
    this.resetPasswordFormTouchControl(formControl, content, touchValue);
  }

  static touchControlsOnForgotLoginSubmit(
    forgotLoginContent: ForgotLoginScreenContent
  ) {
    for (const key of Object.keys(forgotLoginContent.formGroup.controls)) {
      const control = forgotLoginContent.formGroup?.controls[key];
      if (control.enabled && control.untouched) {
        control.markAsTouched();
        this.touchForgotLoginControl(key, forgotLoginContent);
      }
    }
  }

  static touchControlsCreateMyPasswordForm(content: CreateMyPasswordContent) {
    for (const key of Object.keys(content.formGroup.controls)) {
      const control = content.formGroup?.controls[key];
      if (control.enabled && control.untouched) {
        control.markAsTouched();
        this.touchCreateMyPasswordControl(key, content);
      }
    }
  }

  static touchSecurityControlsOnSubmit(securityContent: sqaContent) {
    for (const key of Object.keys(securityContent.formGroup.controls)) {
      const control = securityContent.formGroup?.controls[key];
      if (control.enabled && control.untouched) {
        control.markAsTouched();
        this.touchControlSecurityscreen(key, securityContent);
      }
    }
  }

  static touchControlsAddEmailAddressOnSubmit(content: AddEmailAddressContent) {
    for (const key of Object.keys(content.formGroup.controls)) {
      const control = content.formGroup?.controls[key];
      if (control.enabled && control.untouched) {
        control.markAsTouched();
        this.touchControlAddEmailAddress(key, content);
      }
    }
  }

  static touchControlAddSqaHelper(
    fieldName: string,
    content: AddSqaContent
  ): void {
    if (fieldName === 'questionOne') {
      content.formAttributes.questionOneControlTouched = true;
    }
  }

  static touchControlAddSqa(fieldName: string, content: AddSqaContent): void {
    if (fieldName === 'answerOne') {
      content.formAttributes.answerOneControlTouched = true;
    }
    if (fieldName === 'answerTwo') {
      content.formAttributes.answerTwoControlTouched = true;
    }
    if (fieldName === 'questionTwo') {
      content.formAttributes.questionTwoControlTouched = true;
    }
    this.touchControlAddSqaHelper(fieldName, content);
  }

  static addSqaFormControlsOnSubmit(content: AddSqaContent): void {
    for (const key of Object.keys(content.formGroup.controls)) {
      const control = content.formGroup?.controls[key];
      if (control.enabled && control.untouched) {
        control.markAsTouched();
        this.touchControlAddSqa(key, content);
      }
    }
  }
}
